import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';
import { SEO } from '../components/SEO';
import { blogArticles } from '../data/blogData';

export const BlogPost = () => {
  const { slug } = useParams();
  const article = blogArticles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-16 min-h-[60vh] flex items-center justify-center" data-testid="blog-not-found">
        <div className="text-center">
          <h1 className="text-2xl font-medium text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Article Not Found
          </h1>
          <p className="text-slate-600 mb-6">The article you're looking for doesn't exist.</p>
          <Link to="/blog" className="text-teal-600 hover:text-teal-700 font-medium">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16" data-testid={`blog-post-${slug}`}>
      <SEO
        title={`${article.title} | Turcoaz Apps Blog`}
        description={article.description}
        canonical={`/blog/${article.slug}`}
      />

      {/* Header */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-teal-600 transition-colors mb-8"
            data-testid="blog-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
          <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2.5 py-1 rounded-full">
            {article.category}
          </span>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight font-semibold text-slate-900 mt-4 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {article.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <article
            className="prose prose-slate max-w-none"
            dangerouslySetInnerHTML={{ __html: article.content }}
            data-testid="blog-post-content"
          />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Try Smart PDF Tools
          </h2>
          <p className="text-slate-600 mb-6">
            Merge, split, compress, and convert PDFs for free. Secure processing, no file storage.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://pdf.turcoazapps.com"
              className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-3.5 rounded-md text-base font-medium hover:bg-slate-800 transition-colors"
              data-testid="blog-post-cta"
            >
              Open Smart PDF Tools
            </a>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium"
            >
              More Articles
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
